import type { GetServerSideProps } from 'next'
import { post, httpDelete, put } from 'utils/request'
import { Row, Col, Modal, Form, Input, message, Select, Popconfirm, Switch, InputNumber, Popover, Dropdown, Radio, Checkbox, Divider } from 'antd'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import useAdmin from '@/hooks/swr/useAdmin'
import useMatchOwner from '@/hooks/swr/useMatchOwner'
import { useChannel } from '@/hooks/useWebSocket'
import ActionsLayout from '@/components/MatchLayout'
import { useMatch } from '@/hooks/swr/useMatch'
import XLSX from 'xlsx'
import { random, orderBy, uniqBy, findIndex, set, clone, max, sum } from 'lodash-es'
import { ColumnsType } from 'antd/es/table'
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
  TeamOutlined,
  DownOutlined,
  TableOutlined,
  AppstoreOutlined,
  UploadOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import { useTeams } from '@/hooks/swr/useTeam'
import WechatShare from '@/components/WechatShare'
import produce from 'immer'
import useLogin from '@/hooks/swr/useLogin'
import Button from '@/components/Button'
import { debouncedFunction } from '@/utils/debouncedFunction'
import { teamType } from '@/utils/types'
import { isSinglePlayerMatch } from '@/utils/utils'
import { ItemType } from 'antd/es/menu/interface'
import Image from 'next/image'
import ColumnResizeTable from '@/components/ColumnResizeTable'
import { useAllRounds } from '@/hooks/swr/useRound'
import { CopyToClipboard } from 'react-copy-to-clipboard'

type PropType = { match_id: string }

const TEST_TEAM_COUNT = 20
const TEST_MEMBER_NAMES = [
  '张伟',
  '王伟',
  '李伟',
  '刘伟',
  '陈伟',
  '杨伟',
  '赵伟',
  '黄伟',
  '周伟',
  '吴伟',
  '徐伟',
  '孙伟',
  '马伟',
  '胡伟',
  '郭伟',
  '何伟',
  '高伟',
  '林伟',
  '罗伟',
  '梁伟',
  '宋伟',
  '郑伟',
  '谢伟',
  '唐伟',
  '许伟',
  '邓伟',
  '冯伟',
  '韩伟',
  '曹伟',
  '曾伟',
  '王芳',
  '李芳',
  '张敏',
  '刘敏',
  '陈敏',
  '杨敏',
  '赵敏',
  '黄敏',
  '周敏',
  '吴敏',
  '徐敏',
  '孙敏',
  '马敏',
  '胡敏',
  '郭敏',
  '何敏',
  '高敏',
  '林敏',
  '罗敏',
  '梁敏',
  '宋敏',
  '郑敏',
  '谢敏',
  '唐敏',
  '许敏',
  '邓敏',
  '冯敏',
  '韩敏',
  '曹敏',
  '曾敏',
]

const getTestTeamSize = (matchType?: string) => {
  if (!matchType) return 2
  if (isSinglePlayerMatch(matchType)) return 1
  if (matchType === 'zhong_hua_qiao_pai') return 3
  if (matchType === 'duplicated_team' || matchType === 'four_players_team' || matchType === 'league') return 4
  return 2
}

const getTestNames = (count: number, existingNames: Set<string>) => {
  const result: string[] = []
  for (let i = 0; result.length < count; i += 1) {
    const base = TEST_MEMBER_NAMES[i % TEST_MEMBER_NAMES.length]
    const round = Math.floor(i / TEST_MEMBER_NAMES.length)
    const candidate = round > 0 ? `${base}${round + 1}` : base
    if (!existingNames.has(candidate)) {
      result.push(candidate)
    }
  }
  return result
}

const Teams: Next.PageWithLayout<PropType> = ({ match_id, is_presentation }) => {
  const [newTeamModalOpen, setNewTeamModalOpen] = useState<boolean>(false)
  const [batchRenameModalOpen, setBatchRenameModalOpen] = useState<boolean>(false)
  const [leagueTeamMoalOpen, setLeagueTeamModalOpen] = useState<boolean>(false)
  const [importMode, setImportMode] = useState<string>('all')
  const [guildIsInput, setGuildIsInput] = useState(false)
  const [warningForUserRepeated, setWarningForUserRepeated] = useState(false)
  const [repeatedStatus, setRepeatedStatus] = useState({ exsit_user1: false, exsit_user2: false, exsit_user3: false, exsit_user4: false })
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list')
  const [batchSmsModal, setBatchSmsModal] = useState<{ open: boolean; variables: string[]; loading: boolean }>({
    open: false,
    variables: ['', '', ''],
    loading: false,
  })
  const [importModalOpen, setImpoertModalOpen] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const { data: allRounds } = useAllRounds(match_id)
  const [importText, setImportText] = useState('')
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [textImportExpanded, setTextImportExpanded] = useState(false)
  const excelFileAccept = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const onGoingRounds = useMemo(() => (allRounds ?? []).filter((r) => r.status !== 'finished'), [allRounds])

  const isTeamArranged = useCallback(
    (teamId: string) => {
      return onGoingRounds
        .map((r) => [r.team1_id, r.team2_id])
        .flat()
        .includes(teamId)
    },
    [onGoingRounds]
  )

  const handleViewModeChange = (mode: 'list' | 'card') => {
    setViewMode(mode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('playersOrTeamsViewMode', mode)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedViewMode = localStorage.getItem('playersOrTeamsViewMode')
      if (savedViewMode === 'list' || savedViewMode === 'card') {
        setViewMode(savedViewMode)
      }
    }
  }, [])

  const [order, setOrder] = React.useState('')
  const { isAdmin } = useAdmin(match_id)
  const { isMatchOwner } = useMatchOwner(match_id)
  const { current_user } = useLogin()

  const [editTeamInfo, setEditTeamInfo] = useState<teamType>(undefined)

  const { data: match } = useMatch(match_id)
  const { data: teams, mutate: mutateTeams } = useTeams(match_id, undefined, match.is_encrypted)
  const [filterKeywords, setFilterKeywords] = useState('')

  const sorter = (a, b) => {
    let sortNum = 0
    if (a.check_in && !b.check_in) {
      sortNum = 1
    }
    if (!a.check_in && b.check_in) {
      sortNum = -1
    }
    if (!a.give_up && b.give_up) {
      sortNum = 1
    }
    if (a.give_up && !b.give_up) {
      sortNum = -1
    }
    return sortNum
  }

  const getComparator = useCallback((order) => (a, b) => order === 'desc' ? sorter(a, b) : order === 'asc' ? -sorter(a, b) : 0, [])

  const sortByOrder = (teams, comparator) => {
    const sortTeams = teams.map((v, index) => [v, index])
    sortTeams.sort((a, b) => {
      const order = comparator(a[0], b[0])
      if (order !== 0) {
        return order
      }
      return a[1] - b[1]
    })
    return sortTeams.map((v) => v[0])
  }

  const sortedTeams = useMemo(() => {
    const filteredTeams = (teams ?? []).filter(
      (t) =>
        t.mobile_user1?.includes(filterKeywords) || t.mobile_user1?.includes(filterKeywords) || t.name_user1?.includes(filterKeywords) || t.name_user2?.includes(filterKeywords)
    )
    return orderBy(filteredTeams, [is_presentation && 'check_in', 'created_at'])
  }, [filterKeywords, is_presentation, teams])

  const tableRows: teamType[] = useMemo(() => sortByOrder(sortedTeams, getComparator(order)), [getComparator, order, sortedTeams])

  const members = useMemo(
    () =>
      teams
        .flatMap((team) => [
          { mobile: team.mobile_user1, name: team.name_user1 },
          { mobile: team.mobile_user2, name: team.name_user2 },
        ])
        .filter((member) => (member.mobile ?? '').length > 0),
    [teams]
  )

  useChannel(`user-to-match-${match_id}`, true, (obj) => {
    switch (obj.action) {
      case 'updateTeamCheckIn':
        mutateTeams(
          (cachedTeams) =>
            produce(cachedTeams, (draft) => {
              const i = findIndex(draft, (team) => team.id === obj.data.team_id)
              if (i >= 0) {
                draft[i].check_in = true
              }
              return draft
            }),
          false
        )
        break
      default:
    }
  })

  useChannel(`match-${match_id}`, true, (obj) => {
    switch (obj.action) {
      case 'createTeam':
        mutateTeams((cachedTeams) => uniqBy([...cachedTeams, obj.data.team], (t) => t.id), false)
        break
      case 'deleteTeam':
        mutateTeams((cachedTeams) => cachedTeams.filter((team) => team.id !== obj.data.team_id), false)
        break

      case 'mutateTeams':
        mutateTeams()
        break
      case 'updateTeam':
        mutateTeams(
          (cachedTeams) =>
            produce(cachedTeams, (draft) => {
              const i = findIndex(draft, (team) => team.id === obj.data.team.id)
              if (i >= 0) {
                draft[i] = obj.data.team
              }
            }),
          false
        )
        break
      default:
    }
  })

  const postNewTeam = useCallback(
    (value) => {
      if (value.name_user1 === value.name_user2) {
        message.info('队长队员一致，请您重试')
        return
      }
      post('users/teams', { match_id, ...value }).then((res) => {
        if (res.ok) {
          setNewTeamModalOpen(false)
          mutateTeams()
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutateTeams]
  )

  const batchPostNewTeam = useCallback(
    (teams: teamType[], useExcelName = false, overWrite = false) => {
      post('users/teams/batch', { teams, match_id, useExcelName, overWrite }).then((res) => {
        setImportLoading(false)
        if (res.ok) {
          setImpoertModalOpen(false)
          message.info(`成功导入${res.new_teams.length}队，过滤重复队伍${res.repeated_teams_count}队`, 10)
          mutateTeams()
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutateTeams]
  )
  const batchRename = useCallback(
    (value) => {
      post('users/teams/batch_rename', { match_id, ...value }).then((res) => {
        if (res.ok) {
          mutateTeams()
        } else {
          message.info(res.message)
        }
      })
    },
    [match_id, mutateTeams]
  )

  const deleteTeam = useCallback(
    (delete_team_id: string) => {
      if (isTeamArranged(delete_team_id)) {
        message.error('已编排的队伍不能删除')
        return Promise.reject()
      }
      return httpDelete(`users/teams/${delete_team_id}`, { match_id }).then((res) => {
        if (res.ok) {
          mutateTeams()
        } else {
          message.error(res.message, 10)
        }
      })
    },
    [match_id, mutateTeams, isTeamArranged]
  )

  const putTeam = useCallback(
    (value) => {
      const trimedValue = { ...value, name_user1: value.name_user1.trim(), name_user2: value.name_user2.trim() }
      put(`users/teams/${editTeamInfo?.id}`, trimedValue).then(() => {
        mutateTeams()
        setEditTeamInfo(undefined)
      })
    },
    [editTeamInfo?.id, mutateTeams]
  )

  const postBatchSms = useCallback((params) => {
    post('matches/send_sms_notifications', { sms_params: params }).then(() => {
      message.success('短信群发成功')
      setBatchSmsModal({ open: false, variables: ['', '', ''], loading: false })
    })
  }, [])

  const onBatchCheckIn = useCallback(() => {
    put(`users/teams/batch_check_in`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('一键签到成功')
        mutateTeams()
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutateTeams])

  const onBatchCheckOut = useCallback(() => {
    put(`users/teams/batch_check_out`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('一键取消签到成功')
        mutateTeams()
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutateTeams])

  const onResetAllScore = useCallback(() => {
    put(`matches/reset_all_scores`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('重置分数成功')
        mutateTeams()
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutateTeams])

  const onBatchTeamUp = useCallback(() => {
    put(`users/teams/batch_team_up`, { match_id }).then((res) => {
      if (res.ok) {
        message.info('一键配对成功')
        mutateTeams()
      }
    })
  }, [match_id, mutateTeams])

  const onToggleSeed = useCallback(
    (team) => {
      put(`users/teams/set_seed`, { match_id, team_id: team.id, is_seed: !team.is_seed }).then((res) => {
        if (res.ok) {
          message.success(!team.is_seed ? '已标记为种子队' : '已取消种子队')
          mutateTeams()
        } else {
          message.error(res.message)
        }
      })
    },
    [match_id, mutateTeams]
  )

  const onGenerateTestTeams = useCallback(() => {
    if (!match?.is_test_match) {
      message.info('仅测试赛可用')
      return
    }
    const teamSize = getTestTeamSize(match.match_type)
    const existingNames = new Set(
      (teams ?? [])
        .flatMap((team) => [team.name_user1, team.name_user2, team.name_user3, team.name_user4])
        .filter((name) => (name ?? '').length > 0)
    )
    const names = getTestNames(TEST_TEAM_COUNT * teamSize, existingNames)
    const existingIndices = (teams ?? [])
      .map((team) => parseInt(team.name, 10))
      .filter((value) => !Number.isNaN(value))
    const startIndex = (existingIndices.length ? Math.max(...existingIndices) : 0) + 1
    const newTeams: teamType[] = Array.from({ length: TEST_TEAM_COUNT }).map((_, index) => {
      const base = index * teamSize
      return {
        name: `${startIndex + index}队`,
        name_user1: names[base] ?? `测试队员${base + 1}`,
        mobile_user1: '',
        name_user2: teamSize > 1 ? names[base + 1] ?? `测试队员${base + 2}` : '',
        mobile_user2: '',
        name_user3: teamSize > 2 ? names[base + 2] ?? `测试队员${base + 3}` : '',
        mobile_user3: '',
        name_user4: teamSize > 3 ? names[base + 3] ?? `测试队员${base + 4}` : '',
        mobile_user4: '',
        gender: '',
        score: 0,
        guild: '',
      }
    }) as teamType[]
    batchPostNewTeam(newTeams, true, false)
  }, [batchPostNewTeam, match?.is_test_match, match?.match_type, teams])

  const exportExcel = useCallback(() => {
    message.info('正在导出样例表')
    const workbook = XLSX.utils.book_new()
    let header = ['队名', '队员1', '队员1手机号', '队员2', '队员2手机号', '团体名']

    if (match?.match_type === 'zhong_hua_qiao_pai') {
      header = ['队名', '队员1', '队员1手机号', '队员2', '队员2手机号', '队员3', '队员3手机号', '团体名']
    }
    if (match?.match_type === 'duplicated_team' || match?.match_type === 'four_players_team') {
      header = ['队名', '队员1', '队员1手机号', '队员2', '队员2手机号', '队员3', '队员3手机号', '队员4', '队员4手机号', '团体名']
    }
    if (match?.group_by_gender) {
      header.push('性别组')
    }
    const worksheet = XLSX.utils.json_to_sheet([], {
      header,
    })
    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet1')
    XLSX.write(workbook, {
      sheet: 'sheet1',
      type: 'base64',
      cellDates: true,
      bookType: 'xlsx',
    })

    if (workbook) {
      XLSX.writeFile(workbook, `选手录入表.xlsx`)
    } else {
      message.info('出错了')
    }
  }, [match?.group_by_gender, match?.match_type])

  const exportTeamInfo = useCallback(() => {
    const workbook = XLSX.utils.book_new()

    let teamJson = sortedTeams.map((team) => ({
      队名: team.name,
      队员1: team.name_user1,
      队员1手机号: team.mobile_user1,
      队员2: team.name_user2,
      队员2手机号: team.mobile_user2,
      团体名: team.guild,
    }))
    let worksheet = XLSX.utils.json_to_sheet(teamJson, {
      header: ['队名', '队员1', '队员1手机号', '队员2', '队员2手机号', '团体名'],
    })
    if (match?.match_type === 'duplicated_team' || match?.match_type === 'four_players_team') {
      teamJson = sortedTeams.map((team) => ({
        队名: team.name,
        队员1: team.name_user1,
        队员1手机号: team.mobile_user1,
        队员2: team.name_user2,
        队员2手机号: team.mobile_user2,
        队员3: team.name_user3,
        队员3手机号: team.mobile_user3,
        队员4: team.name_user4,
        队员4手机号: team.mobile_user4,
        团体名: team.guild,
      }))
      worksheet = XLSX.utils.json_to_sheet(teamJson, {
        header: ['队名', '队员1', '队员1手机号', '队员2', '队员2手机号', '队员3', '队员3手机号', '队员4', '队员4手机号', '团体名'],
      })
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'sheet1')
    XLSX.write(workbook, {
      sheet: 'sheet1',
      type: 'base64',
      cellDates: true,
      bookType: 'xlsx',
    })

    if (workbook) {
      XLSX.writeFile(workbook, `参赛选手信息表.xlsx`)
    } else {
      message.info('出错了')
    }
  }, [match?.match_type, sortedTeams])

  const importFromLeagueTeams = useCallback(
    (params) => {
      post(`users/teams/create_from_league`, { match_id, ...params }).then((res) => {
        setLeagueTeamModalOpen(false)
        if (res.ok) {
          mutateTeams()
        } else {
          message.info(`error:${res.message}`)
        }
      })
    },
    [match_id, mutateTeams]
  )

  const onBatchDelete = useCallback(() => {
    httpDelete(`users/teams/batch_delete`, { match_id }).then((res) => {
      if (res.ok) {
        mutateTeams()
        message.success('删除成功')
      } else {
        message.error(res.message)
      }
    })
  }, [match_id, mutateTeams])

  const onImportExcel = useCallback(
    (event, useExcelName = false, overWrite = false) => {
      setImportLoading(true)
      const file = event.target.files[0]
      const reader = new FileReader()
      try {
        reader.readAsArrayBuffer(file)
        reader.onload = () => {
          const workbook = XLSX.read(reader.result)
          let ExcelData: object[] = XLSX.utils.sheet_to_json(workbook.Sheets.sheet1)
          if (!ExcelData || ExcelData.length === 0) {
            ExcelData = XLSX.utils.sheet_to_json(workbook.Sheets['队伍信息'])
          }
          ExcelData = ExcelData.filter((teamObject) => teamObject['队员1'] || teamObject['队员2'])
          const newTeams: teamType[] = ExcelData.map((teamObject, index) => ({
            name: teamObject['队名'] ?? `${index + 1}队`,
            name_user1: teamObject['队员1'] ?? '',
            mobile_user1: teamObject['队员1手机号'] ?? '',
            name_user2: teamObject['队员2'] ?? '',
            mobile_user2: teamObject['队员2手机号'] ?? '',
            name_user3: teamObject['队员3'] ?? '',
            mobile_user3: teamObject['队员3手机号'] ?? '',
            name_user4: teamObject['队员4'] ?? '',
            mobile_user4: teamObject['队员4手机号'] ?? '',
            gender: teamObject['性别组'] ?? '',
            score: 0,
            guild: teamObject['团体名'],
          })) as teamType[]
          if (newTeams.length > 0) {
            batchPostNewTeam(newTeams, useExcelName, overWrite)
          } else {
            setImportLoading(false)
            message.info('请检查是否按照系统格式填写表格')
          }
        }
        if (event?.target) {
          event.target.value = ''
        }
      } catch (error) {
        console.error(error)
      }
    },
    [batchPostNewTeam]
  )

  const excelImportOptions = useMemo(
    () => [
      {
        key: 'auto',
        title: '自动生成队名',
        description: '无需提前准备队名，系统会按照导入顺序生成“1队、2队...”',
        helper: '最适合第一次导入名单',
        badge: '推荐',
        icon: <AppstoreOutlined className="text-blue-600" />,
        buttonText: '上传Excel',
        handler: (e) => onImportExcel(e),
        highlight: true,
        showTemplateLink: true,
      },
      {
        key: 'respect',
        title: '沿用表格中的队名',
        description: 'Excel 中的队名会被完整保留，适合已整理好编号的线下赛事。',
        helper: '系统会读取“队名”列',
        icon: <TableOutlined className="text-blue-500" />,
        buttonText: '使用Excel队名',
        handler: (e) => onImportExcel(e, true),
        highlight: false,
      },
      {
        key: 'overwrite',
        title: '覆盖现有队名',
        description: '重新命名系统中已有的队伍，数据会按照Excel内容统一更新。',
        helper: '会覆盖已有队名，请谨慎操作',
        icon: <SyncOutlined className="text-blue-500" />,
        buttonText: '覆盖队名并导入',
        handler: (e) => onImportExcel(e, true, true),
        highlight: false,
      },
    ],
    [onImportExcel]
  )
  const [primaryExcelOption, ...secondaryExcelOptions] = excelImportOptions
  const renderExcelOptionCard = useCallback(
    (option) => (
      <div key={option.key} className={`rounded-2xl border p-4 shadow-sm ${option.highlight ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-gray-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-base font-semibold text-gray-900">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full shadow ${option.highlight ? 'bg-blue-100' : 'bg-white'}`}>{option.icon}</div>
            {option.title}
          </div>
          {option.badge && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${option.highlight ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`}>{option.badge}</span>
          )}
        </div>
        <p className="mt-2 text-sm text-gray-600">{option.description}</p>
        <Button
          className={`relative mt-4 flex h-11 w-full items-center justify-center rounded-xl border-2 font-semibold ${
            option.highlight ? 'border-blue-500 text-blue-600 hover:border-blue-600 hover:text-blue-700' : 'border-gray-300 text-gray-700'
          }`}
        >
          <UploadOutlined className="mr-2 text-base" />
          {option.buttonText}
          <input type="file" className="absolute left-0 top-0 h-full w-full cursor-pointer opacity-0" accept={excelFileAccept} onChange={option.handler} />
        </Button>
        {option.showTemplateLink && (
          <Button type="link" className="mt-2 p-0 text-sm font-semibold text-blue-600 underline underline-offset-2" onClick={exportExcel}>
            下载Excel样例表
          </Button>
        )}
        <div className="mt-2 text-xs text-gray-400">{option.helper}</div>
      </div>
    ),
    [excelFileAccept, exportExcel]
  )

  const wechatShare = useMemo(() => {
    if (match.name) {
      return <WechatShare res={{ title: match.name }} />
    }
    return null
  }, [match.name])

  const OptionItems: ItemType[] = [
    {
      key: 'team-management',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">队伍管理</div>,
      type: 'group',
      children: [
        {
          key: '10',
          label: (
            <div
              className={`flex items-center rounded-md px-1 py-2 transition-colors ${match.match_type === 'league' && 'opacity-50'}`}
              onClick={() => {
                if (match.match_type !== 'league') setNewTeamModalOpen(true)
              }}
            >
              <PlusOutlined className="mr-2" />
              <span>新建队伍</span>
            </div>
          ),
        },
        {
          key: '20',
          label: (
            <div
              className={`flex items-center rounded-md px-1 py-2 transition-colors ${match.match_type === 'league' && 'opacity-50'}`}
              onClick={() => {
                if (match.match_type !== 'league') setBatchRenameModalOpen(true)
              }}
            >
              <EditOutlined className="mr-2" />
              <span>重新编号</span>
            </div>
          ),
        },
        {
          key: '30',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() =>
                Modal.confirm({
                  content: '您确定要一键全签到吗',
                  onOk: () => {
                    onBatchCheckIn()
                  },
                })
              }
            >
              <CheckOutlined className="mr-2" />
              <span>一键签到</span>
            </div>
          ),
        },
        {
          key: '32',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() =>
                Modal.confirm({
                  content: '您确定要一键全取消签到吗',
                  onOk: () => {
                    onBatchCheckOut()
                  },
                })
              }
            >
              <CloseOutlined className="mr-2" />
              一键取消签到
            </div>
          ),
        },
        {
          key: '35',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() =>
                Modal.confirm({
                  content: '您确定要一键配对吗',
                  onOk: () => {
                    onBatchTeamUp()
                  },
                })
              }
            >
              <SyncOutlined className="mr-2" />
              <span>一键配对</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'export-import',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">导出导入</div>,
      type: 'group',
      children: [
        {
          key: '40',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setExportModalOpen(true)}>
              <ExportOutlined className="mr-2" />
              <span>导出队伍信息</span>
            </div>
          ),
        },
        {
          key: '50',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => exportExcel()}>
              <DownloadOutlined className="mr-2" />
              <span>导出样例表</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'batch-operations',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">批量操作</div>,
      type: 'group',
      children: [
        {
          key: '60',
          label: (
            <div
              className={`flex items-center rounded-md px-1 py-2 transition-colors  ${match.match_type === 'league' && 'opacity-50'}`}
              onClick={() => {
                if (match.match_type !== 'league') setImpoertModalOpen(true)
              }}
            >
              <DownloadOutlined className="mr-2" />
              <span>批量导入</span>
            </div>
          ),
        },
        match.match_type === 'league' && {
          key: '65',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setLeagueTeamModalOpen(true)}>
              <TeamOutlined className="mr-2" />
              <span>从联赛名单导入</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'admin-operations',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">管理员操作</div>,
      type: 'group',
      children: [
        current_user.is_gd_admin && {
          key: '68',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => onResetAllScore()}>
              <ReloadOutlined className="mr-2" />
              <span>重置分数</span>
            </div>
          ),
        },
        current_user.is_gd_admin && {
          key: '70',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => setBatchSmsModal({ ...batchSmsModal, open: true })}>
              <MessageOutlined className="mr-2" />
              <span>群发短信</span>
            </div>
          ),
        },
        {
          key: '80',
          label: (
            <div className="flex items-center rounded-md px-1 py-2 transition-colors" onClick={() => window.open(`/match/${match.id}/pay_batch_sms`)}>
              <MessageOutlined className="mr-2" />
              <span>付费群发短信</span>
            </div>
          ),
        },
      ],
    },
    {
      key: 'owner-operations',
      label: <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">比赛创建者操作</div>,
      type: 'group',
      children: [
        (current_user.is_gd_admin || isMatchOwner) && {
          key: '62',
          label: (
            <div
              className="flex items-center rounded-md px-1 py-2 transition-colors"
              onClick={() => {
                const arrangedTeams = teams?.filter((team) => isTeamArranged(team.id)) ?? []
                if (arrangedTeams.length > 0) {
                  Modal.info({
                    title: '无法批量删除',
                    content: (
                      <div>
                        <div>当前有 {arrangedTeams.length} 个队伍处于"已编排"状态。</div>
                        <div>请先在桌次编排中移除这些队伍后再进行批量删除。</div>
                      </div>
                    ),
                    okText: '知道了',
                  })
                  return
                }
                Modal.confirm({
                  content: (
                    <div className="text-red-600">
                      <div>您确定要全部删除吗？此操作不可恢复。</div>
                      <div>若比赛已开始，建议不要删除，以免造成数据丢失。</div>
                    </div>
                  ),
                  type: 'warning',
                  okText: '确认删除',
                  onOk: () => {
                    onBatchDelete()
                  },
                })
              }}
            >
              <DeleteOutlined className="mr-2" />
              <span>批量删除</span>
            </div>
          ),
        },
      ],
    },
  ]

  const showPayment = useMemo(() => isAdmin && !is_presentation && match.entry_fee > 0, [isAdmin, is_presentation, match.entry_fee])

  const teamCard = useCallback(
    (team, index) => {
      return (
        <div key={index} className="w-full rounded-lg bg-white p-3 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <span className="mr-2 text-lg font-bold">{team.name}</span>
              {(() => {
                if (isTeamArranged(team.id)) {
                  return <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600">已编排</span>
                }
                let statusElement = <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-600">已签到</span>
                if (!team.check_in) {
                  statusElement = <span className="inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-600">未签到</span>
                }
                if (team.give_up) {
                  statusElement = <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-600">已弃权</span>
                }
                return statusElement
              })()}
              {match.match_type === 'group_round_robin' && team.is_seed && (
                <span className="ml-1 inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-600">种子</span>
              )}
            </div>
            {isAdmin && !is_presentation && (
              <div className="flex space-x-1">
                {match.match_type === 'group_round_robin' && (
                  <Button
                    size="small"
                    type={team.is_seed ? 'primary' : 'default'}
                    style={team.is_seed ? { background: '#d97706', borderColor: '#d97706' } : undefined}
                    disabled={isTeamArranged(team.id)}
                    onClick={() => onToggleSeed(team)}
                  >
                    {team.is_seed ? '取消种子' : '设为种子'}
                  </Button>
                )}
                <Button size="small" type="primary" ghost onClick={() => setEditTeamInfo(team)}>
                  编辑
                </Button>
                <Popconfirm
                  title={
                    <div className="flex flex-col items-center justify-center">
                      <div>删除后附带的比赛记录也会消失</div>
                      <div>谨慎删除</div>
                    </div>
                  }
                  onCancel={() => {}}
                  okText="确认"
                  cancelText="取消"
                  onConfirm={() => deleteTeam(team.id)}
                  disabled={isTeamArranged(team.id)}
                >
                  <Button size="small" danger disabled={isTeamArranged(team.id)}>
                    删除
                  </Button>
                </Popconfirm>
              </div>
            )}
          </div>

          <div className="mt-2 grid grid-cols-2">
            <div className="flex items-center">
              <span className="text-lg font-bold">{team.name_user1}</span>
            </div>
            {team.name_user2 && (
              <div className="flex items-center">
                <span className="text-lg font-bold">{team.name_user2}</span>
              </div>
            )}
            {team.name_user3 && (
              <div className="flex items-center">
                <span className="text-lg font-bold">{team.name_user3}</span>
              </div>
            )}
            {team.name_user4 && (
              <div className="flex items-center">
                <span className="text-lg font-bold">{team.name_user4}</span>
              </div>
            )}
          </div>

          <div className="mt-1 flex flex-col text-sm">
            {match.match_type === 'duplicated' && (
              <div className={`${team.direction === 'east_west' ? 'text-red-500' : 'text-blue-500'} font-medium`}>{team.direction === 'east_west' ? '东西' : '南北'}</div>
            )}
            <div className="flex w-full items-center gap-1 truncate">
              <div>团体:</div>
              {team.guild && <div className="text-xs text-gray-600">{team.guild}</div>}
            </div>
            {showPayment && <div className="mt-1 font-medium">{team.has_paid ? `${team.payment_amount / 100}元` : '无支付'}</div>}
          </div>
        </div>
      )
    },
    [deleteTeam, isAdmin, is_presentation, isTeamArranged, match.match_type, showPayment, onToggleSeed]
  )

  const createTeamsComponent = useCallback(() => {
    if (match.match_type === 'league') {
      return <div />
    }

    return (
      <div>
        <div className="flex flex-col items-center">
          <div className="mt-10 text-3xl font-bold text-white">暂无队伍</div>
          <div className="text-semibold mt-4 font-bold text-white">下载样例表即可一键导入</div>
          <Image alt="下载样例表" src="/import-teams.jpg" width={320} height={200} />
        </div>
        <div className="flex justify-center">
          <Button
            className="mt-1 h-16 w-60 rounded-xl bg-gray-100 text-xl font-semibold text-primary"
            onClick={() => {
              setNewTeamModalOpen(true)
            }}
          >
            手动新增一队
          </Button>
          {match?.is_test_match && (
            <Button
              className="ml-10 mt-1 h-16 w-60 rounded-xl bg-white text-xl font-semibold text-primary"
              onClick={() =>
                Modal.confirm({
                  content: `确认生成约${TEST_TEAM_COUNT}支测试队伍吗？`,
                  onOk: () => onGenerateTestTeams(),
                })
              }
            >
              一键生成测试队伍
            </Button>
          )}
          <div className="ml-10 flex flex-col">
            <Button className="ml-1 mt-1 h-16 w-60 rounded-xl  border-none text-xl font-semibold text-primary" onClick={() => setImpoertModalOpen(true)}>
              批量导入队伍
              <Image alt="下载样例表" src="/recommend-mark.jpg" width={40} height={40} className="absolute left-0 top-0" />
            </Button>
            <Button className="mt-1 h-fit w-60  text-xl text-white" onClick={() => exportExcel()} type="link">
              <span className="border-0 border-b border-solid border-white font-semibold">下 载 样 例 表</span>
            </Button>
          </div>
        </div>
      </div>
    )
  }, [exportExcel, match.match_type, match?.is_test_match, onGenerateTestTeams])

  const columns = useMemo(() => {
    const baseColumns: ColumnsType<any> = [
      {
        title: '队号',
        dataIndex: 'name',
        key: 'name',
        width: 70,
        fixed: 'left',
      },

      {
        title: '队员1',
        dataIndex: 'name_user1',
        key: 'name_user1',
        width: 150,
        render: (_, team) => {
          return <div className="truncate font-bold">{team.name_user1}</div>
        },
      },
      {
        title: '队员2',
        dataIndex: 'name_user2',
        key: 'name_user2',
        width: 150,
        render: (_, team) => {
          return <div className="truncate font-bold">{team.name_user2}</div>
        },
      },
    ]
    if (match?.match_type === 'zhong_hua_qiao_pai') {
      baseColumns.push({
        title: '队员3',
        dataIndex: 'name_user3',
        key: 'name_user3',
        width: 150,
        render: (_, team) => {
          return <div className="truncate font-bold">{team.name_user3}</div>
        },
      })
    }

    if (match?.match_type === 'duplicated_team' || match?.match_type === 'four_players_team' || match?.match_type === 'league') {
      baseColumns.push(
        {
          title: '队员3',
          dataIndex: 'name_user3',
          key: 'name_user3',
          width: 150,
          render: (_, team) => {
            return <div className="truncate font-bold">{team.name_user3}</div>
          },
        },
        {
          title: '队员4',
          dataIndex: 'name_user4',
          key: 'name_user4',
          width: 150,
          render: (_, team) => {
            return <div className="truncate font-bold">{team.name_user4}</div>
          },
        }
      )
    }

    baseColumns.push({
      title: '团体',
      dataIndex: 'guild',
      key: 'guild',
      width: 300,
      render: (_, team) => {
        return <div className="truncate">{team.guild}</div>
      },
    })

    if (showPayment) {
      baseColumns.push({
        title: '收费',
        dataIndex: 'payment_amount',
        key: 'payment_amount',
        width: 150,
        render: (_, team) => {
          if (team.tokens_amount > 0) {
            return <span>{team.tokens_amount}点</span>
          }
          if (team.has_paid) {
            return <span>{team.payment_amount / 100}元</span>
          }
          return <span className="text-gray-500">无支付</span>
        },
      })
    }

    if (match?.group_by_gender) {
      baseColumns.push({
        title: '组别',
        dataIndex: 'gender',
        key: 'gemder',
        width: 150,
        render: (_, team) => {
          return <div className="truncate font-bold">{team.gender}</div>
        },
      })
    }

    if (match?.match_type === 'duplicated') {
      baseColumns.push({
        title: '方位',
        dataIndex: 'direction',
        key: 'direction',
        width: 100,
        render: (direction) => (direction === 'east_west' ? <span>东西</span> : <span>南北</span>),
      })
    }
    const isGroupRoundRobin = match?.match_type === 'group_round_robin'
    baseColumns.push({
      title: '状态',
      key: 'status',
      width: 120,
      align: 'center',
      render: (_, team) => {
        if (isTeamArranged(team.id)) {
          return <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600">已编排</span>
        }

        if (team.give_up) {
          return <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-600">已弃权</span>
        }
        return (
          <div className="flex items-center justify-center gap-1">
            {isGroupRoundRobin && team.is_seed && <span className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-600">种子</span>}
            {team.check_in ? (
              <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-600">已签到</span>
            ) : (
              <span className="inline-block rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-600">未签到</span>
            )}
          </div>
        )
      },
    })

    if (isAdmin && !is_presentation) {
      baseColumns.push({
        title: '操作',
        key: 'actions',
        fixed: 'right',
        width: isGroupRoundRobin ? 190 : 110,
        render: (_, team) => (
          <div className="flex space-x-1">
            {isGroupRoundRobin && (
              <Button
                size="small"
                type={team.is_seed ? 'primary' : 'default'}
                style={team.is_seed ? { background: '#d97706', borderColor: '#d97706' } : undefined}
                disabled={isTeamArranged(team.id)}
                onClick={() => onToggleSeed(team)}
              >
                {team.is_seed ? '取消种子' : '设为种子'}
              </Button>
            )}
            <Button size="small" type="primary" ghost onClick={() => setEditTeamInfo(team)}>
              编辑
            </Button>
            <Popconfirm
              title={
                <div className="flex flex-col items-center justify-center">
                  <div>删除后附带的比赛记录也会消失</div>
                  <div>谨慎删除</div>
                </div>
              }
              onCancel={() => {}}
              okText="确认"
              cancelText="取消"
              onConfirm={() => deleteTeam(team.id)}
              disabled={isTeamArranged(team.id)}
            >
              <Button size="small" danger disabled={isTeamArranged(team.id)}>
                删除
              </Button>
            </Popconfirm>
          </div>
        ),
      })
    }
    if (window?.innerWidth > 500) {
      baseColumns.push({
        title: '',
        dataIndex: '',
        key: 'hidden',
        fixed: 'right',
        width: 0,
      })
    }

    return baseColumns
  }, [deleteTeam, isAdmin, is_presentation, isTeamArranged, match?.group_by_gender, match?.match_type, showPayment, onToggleSeed])

  const maxTeamIndex = useMemo(() => {
    return max([0, ...sortedTeams.map((t) => parseInt(t.name, 10))])
  }, [sortedTeams])

  const exportText = useMemo(() => {
    let text = ''
    orderBy(teams, 'created_at').forEach((t) => {
      let teamInfo = `${t.name ?? ''}\t${t.name_user1 ?? ''}\t${t.mobile_user1 ?? ''}\t${t.name_user2 ?? ''}\t${t.mobile_user2 ?? ''}\t`
      if (match?.match_type === 'duplicated_team' || match?.match_type === 'four_players_team') {
        teamInfo += `${t.name_user3 ?? ''}\t${t.mobile_user3 ?? ''}\t${t.name_user4 ?? ''}\t${t.mobile_user4 ?? ''}\t`
      }
      teamInfo += `${t.guild ?? ''}\t${t.gender ?? ''}\n`
      text += teamInfo
    })
    return text
  }, [match?.match_type, teams])

  return (
    <div className="h-full w-full">
      {wechatShare}
      <div className="sticky top-6 z-40 mb-6 w-full px-2 lg:px-6">
        <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg bg-stone-800 px-4 py-3 shadow-md lg:py-1">
          <div className="text-xl font-bold text-white md:text-2xl">队伍管理</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input.Search
              placeholder="搜索手机号或名字"
              enterButton={<span className="flex items-center">搜索</span>}
              allowClear
              className="w-full rounded-md md:w-60"
              size="middle"
              onSearch={(value) => {
                setFilterKeywords(value)
              }}
            />
            <div className="grid grid-cols-3 gap-2 md:w-auto md:grid-cols-6">
              <Select
                className="w-full rounded-md md:w-32"
                size="middle"
                value={order}
                onChange={(value) => setOrder(value)}
                options={[
                  { label: '默认顺序', value: '' },
                  { label: '已签到', value: 'desc' },
                  { label: '未签到', value: 'asc' },
                ]}
              />
              <Popover
                content={
                  <div className="p-2">
                    <div className="mb-2 font-medium text-gray-700">队伍统计</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <div className="text-sm text-gray-600">已签到:</div>
                      <div className="font-medium text-green-600">{sortedTeams.filter((t) => t.check_in && !t.give_up).length}</div>
                      <div className="text-sm text-gray-600">未签到:</div>
                      <div className="font-medium text-yellow-600">{sortedTeams.filter((t) => !t.check_in && !t.give_up).length}</div>
                      <div className="text-sm text-gray-600">已弃权:</div>
                      <div className="font-medium text-red-600">{sortedTeams.filter((t) => t.give_up).length}</div>
                    </div>

                    <div className="my-2 font-medium text-gray-700">支付统计</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">金额: {sum(teams.map((t) => t.payment_amount)) / 100}元</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">点数: {sum(teams.map((t) => t.tokens_amount))}点</div>
                  </div>
                }
                placement="bottom"
                trigger="click"
              >
                <Button className="w-full md:w-auto" icon={<TeamOutlined />}>
                  数量统计
                </Button>
              </Popover>
              <Button
                className="w-full bg-gray-100 text-primary md:w-auto"
                onClick={() => {
                  setNewTeamModalOpen(true)
                }}
              >
                手动新增一队
              </Button>
              {match?.is_test_match && (
                <Button
                  className="w-full bg-white text-primary md:w-auto"
                  onClick={() =>
                    Modal.confirm({
                      content: `确认生成约${TEST_TEAM_COUNT}支测试队伍吗？`,
                      onOk: () => onGenerateTestTeams(),
                    })
                  }
                >
                  一键生成测试队伍
                </Button>
              )}
              <Button
                className="w-full md:w-auto"
                icon={viewMode === 'card' ? <TableOutlined /> : <AppstoreOutlined />}
                onClick={() => handleViewModeChange(viewMode === 'card' ? 'list' : 'card')}
              >
                {viewMode === 'card' ? '表格视图' : '卡片视图'}
              </Button>
              {isAdmin ? (
                <Dropdown menu={{ items: OptionItems }} trigger={['click']} dropdownRender={(menu) => <div className="min-w-[220px] rounded-lg bg-white shadow-lg">{menu}</div>}>
                  <Button className="flex w-full items-center justify-center md:w-auto" type="primary">
                    <span>操作</span>
                    <DownOutlined className="ml-1" />
                  </Button>
                </Dropdown>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="px-2 py-2 lg:p-6">
        {tableRows.length > 0 ? (
          viewMode === 'card' ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">{tableRows.map((team, index) => teamCard(team, index))}</div>
          ) : (
            <ColumnResizeTable columns={columns} dataSource={tableRows} />
          )
        ) : (
          createTeamsComponent()
        )}
      </div>
      {newTeamModalOpen && (
        <Modal
          footer={null}
          open={newTeamModalOpen}
          onCancel={() => {
            setNewTeamModalOpen(false)
            setWarningForUserRepeated(false)
          }}
        >
          <Form
            onValuesChange={(__, values) => {
              const name_user1 = values.name_user1?.replaceAll(' ', '')
              const name_user2 = values.name_user2?.replaceAll(' ', '')
              const name_user3 = values.name_user3?.replaceAll(' ', '')
              const name_user4 = values.name_user4?.replaceAll(' ', '')
              const exsit_user1 =
                !!name_user1 &&
                !!teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user1 ||
                    t.name_user2?.replaceAll(' ', '') === name_user1 ||
                    t.name_user3?.replaceAll(' ', '') === name_user1 ||
                    t.name_user4?.replaceAll(' ', '') === name_user1
                )
              const exsit_user2 =
                !!name_user2 &&
                !!teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user2 ||
                    t.name_user2?.replaceAll(' ', '') === name_user2 ||
                    t.name_user3?.replaceAll(' ', '') === name_user2 ||
                    t.name_user4?.replaceAll(' ', '') === name_user2
                )
              const exsit_user3 =
                !!name_user3 &&
                !!teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user3 ||
                    t.name_user2?.replaceAll(' ', '') === name_user3 ||
                    t.name_user3?.replaceAll(' ', '') === name_user3 ||
                    t.name_user4?.replaceAll(' ', '') === name_user3
                )
              const exsit_user4 =
                !!name_user4 &&
                !!teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user4 ||
                    t.name_user2?.replaceAll(' ', '') === name_user4 ||
                    t.name_user3?.replaceAll(' ', '') === name_user4 ||
                    t.name_user4?.replaceAll(' ', '') === name_user4
                )
              if (exsit_user1 || exsit_user2 || exsit_user3 || exsit_user4) {
                setWarningForUserRepeated(true)
                setRepeatedStatus({ exsit_user1, exsit_user2, exsit_user3, exsit_user4 })
              } else {
                setWarningForUserRepeated(false)
              }
            }}
            layout="vertical"
            onFinish={(value) =>
              debouncedFunction(() => {
                if (warningForUserRepeated) {
                  if (
                    (!value.mobile_user1 && repeatedStatus?.exsit_user1) ||
                    (!value.mobile_user2 && repeatedStatus?.exsit_user2) ||
                    (!value.mobile_user3 && repeatedStatus?.exsit_user3) ||
                    (!value.mobile_user4 && repeatedStatus?.exsit_user4)
                  ) {
                    message.warning('人名若重复，需要添加手机号作区分')
                    return
                  }
                }
                postNewTeam(value)
              })
            }
            preserve={false}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="队号(推荐按默认的数字形式)" name="team_name" rules={[{ required: true, message: '请输入队名' }]} initialValue={`${maxTeamIndex + 1}队`}>
                  <Input maxLength={6} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队长(1号)" name="name_user1" rules={[{ required: true, message: '请输入队长名' }]}>
                  <Input placeholder="必填" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队长手机" name="mobile_user1">
                  <Input inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队友(2号)" name="name_user2">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队友手机" name="mobile_user2">
                  <Input inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col span={24}>
                {match.match_type === 'duplicated' && (
                  <Form.Item label="方位" name="direction">
                    <Select
                      options={[
                        ['东西', 'east_west'],
                        ['南北', 'south_north'],
                      ].map(([label, key]) => ({ label, value: key, key }))}
                    />
                  </Form.Item>
                )}
              </Col>
              {match.match_type === 'zhong_hua_qiao_pai' && (
                <>
                  <Col span={12}>
                    <Form.Item label="队友(3号)" name="name_user3">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(3号)手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.match_type === 'four_players_team' && (
                <>
                  <Col span={12}>
                    <Form.Item label="队友(3号)" name="name_user3" rules={[{ required: true, message: '请输入队长名' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(3号)手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(4号)" name="name_user4">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(4号)手机" name="mobile_user4">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.match_type === 'duplicated_team' && (
                <>
                  <Col span={12}>
                    <Form.Item label="闭室队长(3号)" name="name_user3" rules={[{ required: true, message: '请输入队长名' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队长手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队友(4号)" name="name_user4">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队友手机" name="mobile_user4">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.group_by_gender && (
                <Col span={24}>
                  <Form.Item label="组别" name="gender">
                    <Select options={['男子组', '女子组', '混合组'].map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item label="团体" name="guild">
                  {guildIsInput ? (
                    <Input />
                  ) : (
                    <Select options={(match.guild_lists ?? []).map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
                  )}
                </Form.Item>
              </Col>
              <Col>
                <Checkbox className="mb-2" checked={guildIsInput} onChange={(e) => setGuildIsInput(e.target.checked)}>
                  团体是否自填
                </Checkbox>
              </Col>
            </Row>
            {warningForUserRepeated && (
              <div className="mb-4 rounded-lg border-2 border-red-300 bg-gradient-to-r from-red-50 to-orange-50 p-4 shadow-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500">
                      <CloseOutlined className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-red-800">检测到队伍中存在重复人名</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {repeatedStatus?.exsit_user1 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">1号</span>
                        )}
                        {repeatedStatus?.exsit_user2 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">2号</span>
                        )}
                        {repeatedStatus?.exsit_user3 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">3号</span>
                        )}
                        {repeatedStatus?.exsit_user4 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">4号</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit" className="mt-6">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {editTeamInfo && (
        <Modal
          footer={null}
          open={!!editTeamInfo}
          onCancel={() => {
            setEditTeamInfo(undefined)
            setWarningForUserRepeated(false)
          }}
        >
          <Form
            layout="vertical"
            requiredMark={(label, { required }) =>
              required ? (
                <span className="inline-flex items-center gap-1.5">
                  {label}
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                </span>
              ) : label
            }
            onValuesChange={(__, values) => {
              const name_user1 = values.name_user1?.replaceAll(' ', '')
              const name_user2 = values.name_user2?.replaceAll(' ', '')
              const name_user3 = values.name_user3?.replaceAll(' ', '')
              const name_user4 = values.name_user4?.replaceAll(' ', '')
              const other_teams = teams.filter((t) => t.id !== editTeamInfo.id)
              const exsit_user1 =
                !!name_user1 &&
                !!other_teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user1 ||
                    t.name_user2?.replaceAll(' ', '') === name_user1 ||
                    t.name_user3?.replaceAll(' ', '') === name_user1 ||
                    t.name_user4?.replaceAll(' ', '') === name_user1
                )
              const exsit_user2 =
                !!name_user2 &&
                !!other_teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user2 ||
                    t.name_user2?.replaceAll(' ', '') === name_user2 ||
                    t.name_user3?.replaceAll(' ', '') === name_user2 ||
                    t.name_user4?.replaceAll(' ', '') === name_user2
                )
              const exsit_user3 =
                !!name_user3 &&
                !!other_teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user3 ||
                    t.name_user2?.replaceAll(' ', '') === name_user3 ||
                    t.name_user3?.replaceAll(' ', '') === name_user3 ||
                    t.name_user4?.replaceAll(' ', '') === name_user3
                )
              const exsit_user4 =
                !!name_user4 &&
                !!other_teams.find(
                  (t) =>
                    t.name_user1?.replaceAll(' ', '') === name_user4 ||
                    t.name_user2?.replaceAll(' ', '') === name_user4 ||
                    t.name_user3?.replaceAll(' ', '') === name_user4 ||
                    t.name_user4?.replaceAll(' ', '') === name_user4
                )
              if (exsit_user1 || exsit_user2 || exsit_user3 || exsit_user4) {
                setWarningForUserRepeated(true)
                setRepeatedStatus({ exsit_user1, exsit_user2, exsit_user3, exsit_user4 })
              } else {
                setWarningForUserRepeated(false)
              }
            }}
            onFinish={(value) => {
              debouncedFunction(() => {
                if (warningForUserRepeated) {
                  if (
                    (!value.mobile_user1 && repeatedStatus?.exsit_user1) ||
                    (!value.mobile_user2 && repeatedStatus?.exsit_user2) ||
                    (!value.mobile_user3 && repeatedStatus?.exsit_user3) ||
                    (!value.mobile_user4 && repeatedStatus?.exsit_user4)
                  ) {
                    message.warning('人名若重复，需要添加手机号作区分')
                    return
                  }
                }
                putTeam(value)
              })
            }}
            initialValues={editTeamInfo}
            preserve={false}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="队号(推荐按默认的数字形式)" name="name" rules={[{ required: true, message: '请填写队名' }]}>
                  <Input maxLength={6} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="是否弃权" name="give_up" tooltip="开启后该队伍将标记为弃权">
                  <Switch defaultChecked={editTeamInfo.give_up} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="是否签到" name="check_in" tooltip="开启后该队伍视为已签到，可参与编排">
                  <Switch defaultChecked={editTeamInfo.check_in} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队长(1号)" name="name_user1" rules={[{ required: true, message: '请输入队长名' }]}>
                  <Input placeholder="必填" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队长手机" name="mobile_user1">
                  <Input inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队友(2号)" name="name_user2">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="队友手机" name="mobile_user2">
                  <Input inputMode="numeric" />
                </Form.Item>
              </Col>
              <Col span={24}>
                {match.match_type === 'duplicated' && (
                  <Form.Item label="方位" name="direction">
                    <Select
                      options={[
                        ['东西', 'east_west'],
                        ['南北', 'south_north'],
                      ].map(([label, key]) => ({ label, value: key, key }))}
                    />
                  </Form.Item>
                )}
              </Col>
              {match.match_type === 'zhong_hua_qiao_pai' && (
                <>
                  <Col span={12}>
                    <Form.Item label="队友(3号)" name="name_user3">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(3号)手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.match_type === 'four_players_team' && (
                <>
                  <Col span={12}>
                    <Form.Item label="队友(3号)" name="name_user3" rules={[{ required: true, message: '请输入队长名' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(3号)手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(4号)" name="name_user4">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="队友(4号)手机" name="mobile_user4">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.match_type === 'duplicated_team' && (
                <>
                  <Col span={12}>
                    <Form.Item label="闭室队长(3号)" name="name_user3" rules={[{ required: true, message: '请输入队长名' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队长手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队友(4号)" name="name_user4">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="闭室队友手机" name="mobile_user4">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.match_type === 'league' && (
                <>
                  <Col span={12}>
                    <Form.Item label="替补名(3号)" name="name_user3">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="替补手机" name="mobile_user3">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="替补2名(4号)" name="name_user4">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="替补2手机" name="mobile_user4">
                      <Input inputMode="numeric" />
                    </Form.Item>
                  </Col>
                </>
              )}
              {match.group_by_gender && (
                <Col span={24}>
                  <Form.Item label="组别" name="gender">
                    <Select options={['男子组', '女子组', '混合组'].map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                <Form.Item label="团体" name="guild">
                  {guildIsInput ? (
                    <Input />
                  ) : (
                    <Select options={(match.guild_lists ?? []).map((guild, index) => ({ label: guild, value: guild, key: index }))} allowClear className="w-full" />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="参与排名" name="rankable" tooltip="关闭后该队伍不计入积分排名">
                  <Switch defaultChecked={editTeamInfo.rankable} />
                </Form.Item>
              </Col>
              <Col>
                <Checkbox className="mb-2" checked={guildIsInput} onChange={(e) => setGuildIsInput(e.target.checked)}>
                  团体是否自填
                </Checkbox>
              </Col>
            </Row>
            {warningForUserRepeated && (
              <div className="mb-4 rounded-lg border-2 border-red-300 bg-gradient-to-r from-red-50 to-orange-50 p-4 shadow-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500">
                      <CloseOutlined className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-red-800">检测到队伍中存在重复人名</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {repeatedStatus?.exsit_user1 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">1号</span>
                        )}
                        {repeatedStatus?.exsit_user2 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">2号</span>
                        )}
                        {repeatedStatus?.exsit_user3 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">3号</span>
                        )}
                        {repeatedStatus?.exsit_user4 && (
                          <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-sm font-medium text-white shadow-md">4号</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit" className="mt-4">
                确定
              </Button>
            </Form.Item>
            <Popconfirm
              title={
                <div className="flex flex-col items-center justify-center">
                  <div>删除后附带的比赛记录也会消失</div>
                  <div>谨慎删除</div>
                </div>
              }
              onCancel={() => {}}
              okText="确认"
              cancelText="取消"
              onConfirm={() => {
                deleteTeam(editTeamInfo.id).then(() => {
                  setEditTeamInfo(undefined)
                })
              }}
              disabled={isTeamArranged(editTeamInfo.id)}
            >
              <Button danger disabled={isTeamArranged(editTeamInfo.id)}>删除</Button>
            </Popconfirm>
          </Form>
        </Modal>
      )}
      {batchRenameModalOpen && (
        <Modal footer={null} open={batchRenameModalOpen} onCancel={() => setBatchRenameModalOpen(false)}>
          <Form
            layout="vertical"
            onFinish={(value) => {
              batchRename(value)
              setBatchRenameModalOpen(false)
            }}
            preserve={false}
          >
            <Form.Item label="起始" name="start_number" rules={[{ required: true, message: '请填写起始编号' }]} initialValue={1}>
              <InputNumber />
            </Form.Item>
            <Form.Item label="后缀" name="postfix" rules={[{ required: true, message: '请填写后缀' }]} initialValue="队">
              <Input />
            </Form.Item>
            <Form.Item label="是否打乱" name="shuffle" initialValue={false}>
              <Radio.Group>
                <Radio value={false}>不打乱</Radio>
                <Radio value={true}>打乱</Radio>
              </Radio.Group>
            </Form.Item>
            {match.match_type === 'duplicated' && (
              <Form.Item label="按东西南北分" name="is_direction" initialValue={false}>
                <Radio.Group>
                  <Radio value={false}>不分</Radio>
                  <Radio value={true}>分</Radio>
                </Radio.Group>
              </Form.Item>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {leagueTeamMoalOpen && (
        <Modal open={leagueTeamMoalOpen} onCancel={() => setLeagueTeamModalOpen(false)} footer={[]}>
          <Form
            layout="vertical"
            onFinish={(value) => {
              debouncedFunction(() => {
                importFromLeagueTeams(value)
              })
            }}
            preserve={false}
          >
            <Form.Item label="导入模式" name="mode" initialValue={importMode}>
              <Radio.Group
                onChange={(e) => {
                  setImportMode(e.target.value)
                }}
                value={importMode}
              >
                <Radio value="all">全部</Radio>
                <Radio value="rank">排名</Radio>
                <Radio value="number">数量</Radio>
              </Radio.Group>
            </Form.Item>
            {importMode === 'rank' && (
              <Form.Item label="导入前几队" name="number" rules={[{ required: true, message: '请填写' }]} initialValue={20}>
                <InputNumber />
              </Form.Item>
            )}
            {importMode === 'number' && (
              <div>
                <Form.Item label="第一个导入的编号" name="start_index" rules={[{ required: true, message: '请填写' }]} initialValue={1}>
                  <InputNumber />
                </Form.Item>
                <Form.Item label="共导入几队" name="number" rules={[{ required: true, message: '请填写' }]} initialValue={48}>
                  <InputNumber />
                </Form.Item>
              </div>
            )}
            <Form.Item>
              <Button type="primary" htmlType="submit">
                确定
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {batchSmsModal.open && (
        <Modal open={batchSmsModal.open} onCancel={() => setBatchSmsModal({ ...batchSmsModal, open: false })} footer={null}>
          <div className="flex flex-col items-center justify-center">
            <div className="my-5 mt-10 text-2xl font-bold">短信群发</div>
            <div className="mb-2 w-full font-semibold">短信内容：</div>
            <div className="w-full">
              尊敬的XXX先生/女士，您已成功报名：{' '}
              <Input
                placeholder="****大赛"
                className="w-32 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[0]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 0], e.target.value)}
              />
              ，系统自动默认您参加比赛，如有变动或调整请及时向赛委会提出申请，谢谢！联系方式：公众号「灵缇互娱」。 本次活动比赛将于
              <Input
                placeholder="**月**日 **:** - **:**"
                className="w-36 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[1]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 1], e.target.value)}
              />
              签到（过时未签到者视为自动放弃比赛，按实际情况为准）。预祝您旗开得胜、掼出精彩！{' '}
              <Input
                placeholder="202*年*月*日"
                className="w-32 underline"
                bordered={false}
                defaultValue={batchSmsModal.variables[2]}
                onChange={(e) => set(clone(batchSmsModal), ['variables', 2], e.target.value)}
              />
            </div>
            <div className="mt-3 flex w-full items-start text-xs italic">XXX将替换为系统中参赛选手名称</div>
            <div className="mt-10 font-semibold">{`预计发送短信：${members.length}条`}</div>
            <div className="my-10">
              <Popover
                title="短信发送确认"
                trigger="click"
                content={
                  <div>
                    <div>{`本次将发送${members.length}条短信，一旦确认无法撤销`}</div>
                    <div className="mt-10 flex w-full flex-col items-center justify-center">
                      <Button
                        type="primary"
                        onClick={() => {
                          setBatchSmsModal({ ...batchSmsModal, loading: true })
                          const params = members.map((member) => ({
                            variables: [member.name ?? '', ...batchSmsModal.variables],
                            mobile: member.mobile,
                            user_id: `${member.mobile}-${member.name}-${random(0, 100000)}`,
                          }))
                          postBatchSms(params)
                        }}
                      >
                        确认发送
                      </Button>
                    </div>
                  </div>
                }
              >
                <Button loading={batchSmsModal.loading} type="primary">
                  立即发送
                </Button>
              </Popover>
            </div>
          </div>
        </Modal>
      )}
      {exportModalOpen && (
        <Modal open={exportModalOpen} onCancel={() => setExportModalOpen(false)} footer={false} loading={importLoading}>
          <div className="mt-4 text-center text-2xl font-semibold text-primary">导出队伍信息</div>
          <div>
            <div className="mb-6 text-lg font-semibold">导出表格</div>
            <Button
              className="h-8 w-40 rounded-lg border-2 border-gray-700 text-lg"
              onClick={() => {
                exportTeamInfo()
                setExportModalOpen(false)
              }}
            >
              导出队伍信息表格
            </Button>
            <>
              <Divider />
              <div className="mb-6 text-lg font-semibold">导出文本</div>
              <Input.TextArea
                value={exportText}
                className="mb-6"
                autoSize={{
                  minRows: 5,
                  maxRows: 10,
                }}
              />
              <CopyToClipboard text={exportText} onCopy={() => message.info('复制成功')}>
                <Button className="h-8 w-40 rounded-lg border-2 border-gray-700 text-lg" onClick={() => {}}>
                  复制队伍信息文本
                </Button>
              </CopyToClipboard>
            </>
          </div>
        </Modal>
      )}
      {importModalOpen && (
        <Modal open={importModalOpen} onCancel={() => setImpoertModalOpen(false)} footer={false} loading={importLoading}>
          <div className="px-2 py-4">
            <div className="text-center text-2xl font-semibold text-blue-600">批量导入队伍</div>
            <div className="mt-1 text-center text-sm text-gray-500">根据准备情况选择导入方式，几分钟完成全部队伍录入</div>

            <div className="mt-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Excel 导入</div>
              <div className="mt-3 flex flex-col gap-4">
                {primaryExcelOption && renderExcelOptionCard(primaryExcelOption)}
                {secondaryExcelOptions.length > 0 && <div className="grid gap-4 md:grid-cols-2">{secondaryExcelOptions.map((option) => renderExcelOptionCard(option))}</div>}
              </div>
            </div>

            <Divider plain className="my-8 text-gray-400">
              或
            </Divider>

            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <FileTextOutlined className="text-blue-500" />
                <span>文本导入（高级）</span>
                <span className="text-red-400">(不建议)</span>
                <button
                  type="button"
                  className="ml-auto border-0 bg-transparent p-0 text-xs font-normal text-blue-600 underline underline-offset-2"
                  onClick={() => setTextImportExpanded((prev) => !prev)}
                >
                  {textImportExpanded ? '收起' : '展开'}
                </button>
              </div>
              {textImportExpanded ? (
                <div className="mt-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">每行代表一支队伍：队名 + 队员 + 手机号，可用 Tab 分隔，支持四人队。</p>
                  <Input.TextArea
                    className="mt-4 rounded-xl border border-gray-200 bg-white"
                    autoSize={{
                      minRows: 5,
                    }}
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder={'例：01队\t张三\t138****0000\t李四\t138****1111'}
                  />
                  <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 md:flex-row md:items-center">
                    <Button
                      className="h-11 rounded-xl border-0 bg-brand px-8 text-lg font-semibold text-white hover:bg-brand-dark md:w-auto"
                      onClick={() => {
                        const teamsText = importText.split('\n')
                        const newTeams: teamType[] = teamsText
                          .map((teamText, index) => {
                            const teamObject = teamText.split('\t')
                            const teamResult = {
                              name: teamObject[0] ?? `${index + 1}队`,
                              name_user1: teamObject[1] ?? '',
                              mobile_user1: teamObject[2] ?? '',
                              name_user2: teamObject[3] ?? '',
                              mobile_user2: teamObject[4] ?? '',
                              guild: teamObject[5],
                              gender: teamObject[6] ?? '',
                            } as teamType
                            // 针对一队中有4人的情况
                            if (teamObject.length > 10) {
                              teamResult.name_user3 = teamObject[5] ?? ''
                              teamResult.mobile_user3 = teamObject[6] ?? ''
                              teamResult.name_user4 = teamObject[7] ?? ''
                              teamResult.mobile_user4 = teamObject[8] ?? ''
                              teamResult.guild = teamObject[9]
                              teamResult.gender = teamObject[10] ?? ''
                            }
                            return teamResult
                          })
                          .filter((t) => t.name_user1 || t.name_user2)
                        if (newTeams.length > 0) {
                          batchPostNewTeam(newTeams, false, false)
                        } else {
                          setImportLoading(false)
                          message.info('导入失败')
                        }
                      }}
                    >
                      从文本导入
                    </Button>
                    <div className="text-xs text-gray-400 md:text-left">支持直接从 Excel 粘贴，空行将被忽略。</div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  建议优先使用 Excel 导入，除非需要批量粘贴特殊文本格式。
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

Teams.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

export default Teams
